import type { FunctionalComponent } from 'preact';
import { useMemo } from 'preact/hooks';
import qrcode from 'qrcode-generator';

interface Props {
  /** QR 이 가리킬 주소 */
  value: string;
  /** 접근성 라벨 */
  label?: string;
}

/**
 * QR 코드.
 *
 * 예전에는 호마다 QR 이미지를 미리 만들어 올렸다. 주소가 바뀌면 이미지도 다시
 * 만들어야 했고, 관리자가 이미지 편집 도구를 써야 했다. 이제 **링크만 저장하고
 * 그림은 여기서 그린다.**
 *
 * SVG 로 그리는 이유 — 커버 위에서 크기가 유동적인데, 비트맵은 확대하면 흐려진다.
 * `<path>` 하나로 합쳐 노드 수를 줄인다(모듈 하나에 사각형 하나씩 그리면
 * 29×29 = 841개가 된다).
 */
export const QrCode: FunctionalComponent<Props> = ({ value, label = 'QR 코드' }) => {
  const path = useMemo(() => {
    if (!value) return null;

    // typeNumber 0 = 내용에 맞춰 자동. errorCorrection 'M' 은 인쇄물에서 흔한 수준.
    const qr = qrcode(0, 'M');
    qr.addData(value);
    qr.make();

    const count = qr.getModuleCount();
    const parts: string[] = [];
    for (let row = 0; row < count; row++) {
      for (let col = 0; col < count; col++) {
        if (qr.isDark(row, col)) parts.push(`M${col} ${row}h1v1h-1z`);
      }
    }
    return { d: parts.join(''), count };
  }, [value]);

  if (!path) return null;

  return (
    <svg
      viewBox={`-1 -1 ${path.count + 2} ${path.count + 2}`}
      role="img"
      aria-label={label}
      shape-rendering="crispEdges"
    >
      {/* 여백(quiet zone)이 없으면 인식률이 떨어진다. viewBox 로 1모듈씩 준다. */}
      <rect x="-1" y="-1" width={path.count + 2} height={path.count + 2} fill="#fff" />
      <path d={path.d} fill="#000" />
    </svg>
  );
};
